import { FormEvent, FunctionComponent, useCallback, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import ErrorAlert from '../../../ErrorAlert';
import useCademyError from '../../../../hooks/useCademyError';
import { CademyError } from '@shared/domain-shared';
import Button from '../../../Button';
import styles from './styles.module.scss';
import { CheckSolidIcon } from '../../../Icons';
import Spinner from '../../../Spinner';

type BillingDetails = {
    name: string;
    email: string;
};

export type PaymentType = 'payment' | 'setup';

type CardProps = {
    stripeClientSecret: string | undefined;
    stripeAccountId: string | undefined;
    onPaymentSuccess: () => void;
    billingDetails: BillingDetails;
    paymentType?: PaymentType;
};

const getPaymentFunction = (stripe: Stripe, paymentType: PaymentType) => {
    switch (paymentType) {
        case 'payment':
            return stripe.confirmPayment;
        case 'setup':
            return stripe.confirmSetup;
    }
};

type PaymentFormProps = {
    onPaymentSuccess: () => void;
    billingDetails: BillingDetails;
    paymentType: PaymentType;
};
const PaymentForm: FunctionComponent<PaymentFormProps> = ({
    onPaymentSuccess,
    billingDetails,
    paymentType,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loadingStripe, setLoadingStripe] = useState(true);
    const [processingPayment, setProcessingPayment] = useState(false);
    const { inError, title, detail, setError } = useCademyError();

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (processingPayment) return;
            if (!stripe || !elements) {
                console.error('Stripe or Authentication is not ready yet');
                return;
            }

            setProcessingPayment(true);

            try {
                const paymentFunction = getPaymentFunction(stripe, paymentType);
                const result = await paymentFunction({
                    elements,
                    redirect: 'if_required',
                    confirmParams: {
                        payment_method_data: {
                            billing_details: billingDetails,
                        },
                    },
                });

                if (result.error) {
                    if (
                        result.error.type === 'card_error' ||
                        result.error.type === 'validation_error'
                    ) {
                        // There should be a message from Stripe to display
                        throw new CademyError(
                            `stripe/${result.error.type}`,
                            result.error.message ||
                                'There was an error with your card details. Please try again.',
                            undefined,
                            400
                        );
                    }
                    if (
                        result.error.type === 'invalid_request_error' &&
                        result.error.code === 'payment_intent_authentication_failure'
                    ) {
                        throw new CademyError(
                            `stripe/${result.error.type}`,
                            result.error.message ||
                                'There was an error with your payment. Please try again.',
                            undefined,
                            500
                        );
                    }
                    throw setError(
                        new CademyError(
                            `stripe/${result.error.type}`,
                            'An error occurred while processing your payment. Please try again.',
                            undefined,
                            500
                        )
                    );
                }

                if (
                    'paymentIntent' in result &&
                    (result.paymentIntent.status === 'succeeded' ||
                        result.paymentIntent.status === 'requires_capture')
                ) {
                    return onPaymentSuccess();
                }

                if ('setupIntent' in result && result.setupIntent.status === 'succeeded') {
                    return onPaymentSuccess();
                }
            } catch (error: any) {
                console.error(error);
                setError(error);
                setProcessingPayment(false);
            }
        },
        [
            processingPayment,
            paymentType,
            stripe,
            elements,
            billingDetails,
            setError,
            onPaymentSuccess,
        ]
    );

    return (
        <form onSubmit={handleSubmit} className={styles.container}>
            {loadingStripe ? (
                <Spinner innerClassName={styles.spinnerIn} outerClassName={styles.spinnerOut} />
            ) : null}
            <PaymentElement onReady={() => setLoadingStripe(false)} onLoadError={console.error} />
            <ErrorAlert
                inError={inError}
                title={title}
                detail={detail}
                className={styles.errorAlert}
            />
            <Button
                type="submit"
                preset="primary"
                className={styles.payButton}
                disabled={!stripe || processingPayment}
            >
                <CheckSolidIcon />
                Pay
            </Button>
        </form>
    );
};

export const Card: FunctionComponent<CardProps> = ({
    stripeClientSecret,
    stripeAccountId,
    onPaymentSuccess,
    billingDetails,
    paymentType = 'payment',
}) => {
    if (!stripeClientSecret) {
        console.error('No stripe client secret provided');
        return null;
    }

    const key = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '';
    const stripePromise = loadStripe(key, {
        stripeAccount: stripeAccountId,
    });

    return (
        <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
            <PaymentForm
                onPaymentSuccess={onPaymentSuccess}
                billingDetails={billingDetails}
                paymentType={paymentType}
            />
        </Elements>
    );
};
